import { z } from 'zod';
import {
  CoachPaymentKind,
  CoachSessionPaymentWithIncentive,
  CoachNonSessionPayment,
  coachSessionPaymentWithIncentiveSchema,
  coachNonSessionPaymentSchema,
  CoachPaymentIncentiveName,
  coachPaymentIncentiveNameSchema,
  PayableSession,
  CoachSessionPaymentLike,
  CoachUser,
  coachUserSchema,
  CoachSessionRemarkV2,
  SessionStatus,
  CoachPaymentStatus,
  coachPaymentStatusKind,
  coachPaymentNoteKind,
  CoachPaymentNote,
  CoachPaymentDepositInfoStatus,
  localDepositInfoStatus,
} from '../index.js';
import { PayCycleDTO } from './pay-cycle.dto.js';
import { Temporal } from 'temporal-polyfill';
import { FrequentTimeZones } from '../../temporal-utils/index.js';

const coachPaymentReportPropsSchemaV2 = z.object({
  id: z.string(),
  coach: coachUserSchema,
  payCycleId: z.string(),
  sessions: z.array(coachSessionPaymentWithIncentiveSchema).optional(),
  nonSession: coachNonSessionPaymentSchema.optional(),
  paymentStatus: coachPaymentStatusKind,
  paySentId: z.string().optional(),
  notes: z.array(coachPaymentNoteKind),
});
export type CoachPaymentReportPropsV2 = z.infer<typeof coachPaymentReportPropsSchemaV2>;

export type IncentiveMatrix = Record<CoachPaymentIncentiveName, boolean>;

const amountByIncentive: Record<CoachPaymentIncentiveName, number> = {
  completion: 4_00,
  lateSubstitution: 5_00,
};

export class CoachSessionPaymentDTO implements CoachSessionPaymentLike {
  readonly coachId: string;
  readonly session: PayableSession;

  readonly sessionDuration: Temporal.Duration;
  readonly basePayAmount: number;
  readonly incentives: IncentiveMatrix;

  constructor({ coachId, session }: CoachSessionPaymentLike) {
    this.coachId = coachId;
    this.session = session;

    this.sessionDuration = session.startInstant.until(session.endInstant);
    this.basePayAmount = (() => {
      const totalMinutes = this.totalMinutes;
      switch (totalMinutes) {
        case 30:
          return 8_00;
        case 40:
          return 12_00;
        default: {
          console.warn(`Unrecognized session duration ${totalMinutes} minutes for session ${session.id}`);
          return 0;
        }
      }
    })();

    this.incentives = {
      completion: session.isCompletedOnTime && session.isFeedbackSubmittedOnTime,
      lateSubstitution: (session.coachSessionRemarksV2 ?? []).includes(CoachSessionRemarkV2.subClassWithin12Hours),
    };

    if (!this.isValid()) {
      this.basePayAmount = 0;
      this.incentives = {
        completion: false,
        lateSubstitution: false,
      };
    }
  }

  isValid(): boolean {
    if (!this.session.isCompleted) {
      return false;
    }
    if (this.session.status !== SessionStatus.cancelled && this.session.status !== SessionStatus.archived) {
      return true;
    }
    if (
      this.session.status === SessionStatus.cancelled &&
      this.session.coachSessionRemarksV2?.includes(CoachSessionRemarkV2.canceledWithin12Hours)
    ) {
      return true;
    }

    return false;
  }

  get payCycle(): PayCycleDTO {
    return PayCycleDTO.ongoing(this.session.startInstant.toZonedDateTimeISO(FrequentTimeZones.UsPacific).toPlainDate());
  }

  get totalMinutes(): number {
    return this.sessionDuration.total('minutes');
  }

  get incentivesTotalAmount(): number {
    return coachPaymentIncentiveNameSchema.options.reduce(
      (total, incentive) => total + (this.incentives[incentive] ? amountByIncentive[incentive] : 0),
      0,
    );
  }

  get totalAmount(): number {
    return this.basePayAmount + this.incentivesTotalAmount;
  }

  hasIncentive(incentive: CoachPaymentIncentiveName): boolean {
    return this.incentives[incentive] === true;
  }

  incentiveAmount(incentive: CoachPaymentIncentiveName): number {
    return this.hasIncentive(incentive) ? amountByIncentive[incentive] : 0;
  }

  /**
   * This is a temporary relaxation of types and data. The proper return type of
   * this method should be just `CoachSessionPaymentLike`.
   */
  toJSON(): object {
    return this;
  }
}

/**
 * This represents a generic payment output.
 * A payment can be of any type (teaching, referral,...)
 */
export class CoachPaymentDTO {
  readonly id!: string;
  readonly totalAmountInCents: number;
  readonly type: CoachPaymentKind;

  constructor({ totalAmountInCents, type }: { totalAmountInCents: number; type: CoachPaymentKind }) {
    this.totalAmountInCents = totalAmountInCents;
    this.type = type;
  }

  static fromSessionPayment(sessionPayment: CoachSessionPaymentWithIncentive): CoachPaymentDTO {
    return {
      id: sessionPayment.id,
      totalAmountInCents:
        sessionPayment.paymentAmountInCents +
        sessionPayment.incentives.reduce((total, incentive) => total + incentive.bonusAmountInCents, 0),
      type: 'teaching',
    };
  }

  static fromNonSessionPayment(nonSessionPayment: CoachNonSessionPayment): CoachPaymentDTO {
    return {
      id: nonSessionPayment.id,
      totalAmountInCents: nonSessionPayment.amountInCents,
      type: nonSessionPayment.type,
    };
  }
}
export class CoachPaymentReportDTO {
  readonly id: string;
  readonly coach: CoachUser;
  readonly payCycleId: string;
  private sessions: CoachSessionPaymentWithIncentive[];
  private nonSession: CoachNonSessionPayment | null;
  protected payments!: CoachPaymentDTO[];
  readonly payPeriodInterval: string;
  readonly payDate: string;
  readonly depositPostedDate?: string;
  readonly paymentStatus: CoachPaymentStatus;
  readonly paySentId?: string;
  readonly notes: CoachPaymentNote[];
  readonly depositInfoStatus: CoachPaymentDepositInfoStatus;
  readonly paymentInstrumentID: string | null;

  constructor({
    id,
    coach,
    payCycleId,
    sessions,
    nonSession,
    paymentStatus,
    paySentId,
    notes,
  }: CoachPaymentReportPropsV2) {
    this.id = id;
    this.coach = coach;
    this.payCycleId = payCycleId;
    this.sessions = sessions ?? [];
    this.nonSession = nonSession ?? null;
    this.paymentStatus = paymentStatus;
    this.paySentId = paySentId;
    this.notes = notes;
    this.depositInfoStatus = localDepositInfoStatus(coach.vendorId, coach.paymentInstrumentId);

    this.paymentInstrumentID = coach.paymentInstrumentId;

    this.depositPostedDate = PayCycleDTO.fromPayPeriodId(payCycleId).depositPostedDate;

    if (this.nonSession) {
      this.setNonSession(this.nonSession);
    }
    if (this.sessions.length > 0) {
      this.setSessions(this.sessions);
    }
    if (!this.nonSession && this.sessions.length === 0) {
      this.payments = [];
    }

    const payCycle = PayCycleDTO.fromPayPeriodId(payCycleId);

    this.payPeriodInterval = payCycle.payPeriodInterval;
    this.payDate = payCycle.payDate;
  }

  setSessions(sessions: CoachSessionPaymentWithIncentive[]) {
    this.sessions = sessions;
    this.payments = sessions.map(CoachPaymentDTO.fromSessionPayment);
  }

  setNonSession(nonSession: CoachNonSessionPayment) {
    this.nonSession = nonSession;
    this.payments = [CoachPaymentDTO.fromNonSessionPayment(nonSession)];
  }

  get totalAmountInCents(): number {
    return this.payments.reduce((total, payment) => total + payment.totalAmountInCents, 0);
  }

  get totalSessionsTaught(): number {
    return this.sessions.length;
  }

  get getPayments(): CoachPaymentDTO[] {
    return this.payments;
  }
}
